import { annotationConditionGroupMatches } from './worldAnnotationConditions';
import type { AnnotationRuntimeStateById } from './worldAnnotationRuntime';
import type {
  AnnotationConditionOccupancySnapshot,
  AnnotationStateRecord,
  AnnotationTrigger,
  AnnotationTriggerEffect,
  AnnotationTriggerRuntimeState,
  AnnotationTriggerRuntimeStateByAnnotationId,
  AppliedAnnotationTriggerEffect,
  WorldAnnotation,
} from '../types/worldAnnotation';

export type AnnotationOccupancyByAnnotationId = Record<
  string,
  AnnotationConditionOccupancySnapshot
>;

export const MAX_CHAIN_EVENT_SIGNALS = 32;

export type AnnotationTriggerSignal =
  | {
      kind: 'interact';
      annotationId: string;
      action: string;
      seedStateChange?: AnnotationStateRecord;
    }
  | {
      kind: 'proximity';
      annotationId: string;
      activeEntityIds: string[];
    }
  | {
      kind: 'event';
      eventName: string;
      detail?: AnnotationStateRecord;
    };

export const normalizeActionToken = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '_')
    .replace(/^_+|_+$/g, '');

export const cloneStateRecord = (
  value: AnnotationStateRecord | undefined,
): AnnotationStateRecord | undefined => {
  if (!value) {
    return undefined;
  }
  return Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, entry]));
};

export const cloneAnnotation = (annotation: WorldAnnotation): WorldAnnotation => ({
  ...annotation,
  position: { ...annotation.position },
  ...(annotation.size ? { size: { ...annotation.size } } : {}),
  ...(annotation.tags ? { tags: [...annotation.tags] } : {}),
  ...(annotation.doorTarget ? { doorTarget: { ...annotation.doorTarget } } : {}),
  ...(annotation.state ? { state: cloneStateRecord(annotation.state) } : {}),
});

export const cloneTriggerRuntimeState = (
  value: AnnotationTriggerRuntimeState | undefined,
): AnnotationTriggerRuntimeState | undefined => {
  if (!value) {
    return undefined;
  }
  return {
    ...(Array.isArray(value.activeEntityIds)
      ? { activeEntityIds: [...value.activeEntityIds] }
      : {}),
    ...(typeof value.lastFiredAtMs === 'number' ? { lastFiredAtMs: value.lastFiredAtMs } : {}),
  };
};

export const cloneAnnotationTriggerRuntimeStateById = (
  value: AnnotationTriggerRuntimeStateByAnnotationId | undefined,
): AnnotationTriggerRuntimeStateByAnnotationId | undefined => {
  if (!value) {
    return undefined;
  }

  const annotations = Object.entries(value)
    .map(([annotationId, triggerState]) => {
      const nextTriggerState = Object.entries(triggerState)
        .map(
          ([triggerId, runtimeState]) =>
            [triggerId, cloneTriggerRuntimeState(runtimeState)] as const,
        )
        .filter(
          (entry): entry is [string, AnnotationTriggerRuntimeState] =>
            Boolean(entry[0]) && Boolean(entry[1]),
        );
      return nextTriggerState.length > 0
        ? [annotationId, Object.fromEntries(nextTriggerState)]
        : null;
    })
    .filter(
      (entry): entry is [string, Record<string, AnnotationTriggerRuntimeState>] => entry !== null,
    );

  return annotations.length > 0 ? Object.fromEntries(annotations) : undefined;
};

export const normalizeAnnotationTriggerRuntimeStateById = (
  value: unknown,
): AnnotationTriggerRuntimeStateByAnnotationId | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }

  const annotations = Object.entries(value as Record<string, unknown>)
    .map(([annotationId, triggerState]) => {
      if (!triggerState || typeof triggerState !== 'object' || Array.isArray(triggerState)) {
        return null;
      }

      const nextTriggerState = Object.entries(triggerState as Record<string, unknown>)
        .map(([triggerId, runtimeState]) => {
          if (!runtimeState || typeof runtimeState !== 'object' || Array.isArray(runtimeState)) {
            return null;
          }
          const entry = runtimeState as Record<string, unknown>;
          const lastFiredAtMs = Number(entry.lastFiredAtMs);
          const activeEntityIds = Array.isArray(entry.activeEntityIds)
            ? entry.activeEntityIds
                .map((entityId) => String(entityId).trim())
                .filter((entityId) => entityId.length > 0)
            : undefined;

          return [
            triggerId.trim(),
            {
              ...(Array.isArray(activeEntityIds) && activeEntityIds.length > 0
                ? { activeEntityIds }
                : {}),
              ...(Number.isFinite(lastFiredAtMs)
                ? { lastFiredAtMs: Math.max(0, Math.floor(lastFiredAtMs)) }
                : {}),
            } satisfies AnnotationTriggerRuntimeState,
          ] as const;
        })
        .filter((entry): entry is [string, AnnotationTriggerRuntimeState] => {
          if (!entry) {
            return false;
          }
          return Boolean(entry[0]) && Object.keys(entry[1]).length > 0;
        });

      const trimmedAnnotationId = annotationId.trim();
      if (!trimmedAnnotationId || nextTriggerState.length === 0) {
        return null;
      }
      return [trimmedAnnotationId, Object.fromEntries(nextTriggerState)] as const;
    })
    .filter(
      (entry): entry is readonly [string, Record<string, AnnotationTriggerRuntimeState>] =>
        entry !== null && Boolean(entry[0]),
    );

  return annotations.length > 0 ? Object.fromEntries(annotations) : undefined;
};

export const mergeStateRecord = (
  current: AnnotationStateRecord | undefined,
  patch: AnnotationStateRecord | undefined,
): AnnotationStateRecord | undefined => {
  if (!patch) {
    return cloneStateRecord(current);
  }
  const next = {
    ...(cloneStateRecord(current) ?? {}),
    ...patch,
  };
  return Object.keys(next).length > 0 ? next : undefined;
};

export const mergeStateChangeMap = (
  current: Record<string, AnnotationStateRecord>,
  annotationId: string,
  patch: AnnotationStateRecord | undefined,
): void => {
  if (!patch || Object.keys(patch).length === 0) {
    return;
  }

  current[annotationId] = mergeStateRecord(current[annotationId], patch) ?? {};
};

export const resolveEffectAction = (
  trigger: AnnotationTrigger,
): AppliedAnnotationTriggerEffect['action'] => {
  if (
    (trigger.event === 'proximity_enter' || trigger.event === 'proximity_exit') &&
    'loop' in trigger.effect.payload &&
    trigger.effect.payload.loop === true
  ) {
    return trigger.event === 'proximity_exit' ? 'stop' : 'start';
  }
  return 'pulse';
};

export const shouldRespectCooldown = (action: AppliedAnnotationTriggerEffect['action']): boolean =>
  action !== 'stop';

export const buildAppliedEffect = (params: {
  annotation: WorldAnnotation;
  trigger: AnnotationTrigger;
}): AppliedAnnotationTriggerEffect => ({
  annotationId: params.annotation.id,
  annotationName: params.annotation.name,
  triggerId: params.trigger.id,
  triggerEvent: params.trigger.event,
  action: resolveEffectAction(params.trigger),
  effect: params.trigger.effect,
});

export const setTriggerRuntimeState = (
  runtimeStateById: AnnotationTriggerRuntimeStateByAnnotationId,
  annotationId: string,
  triggerId: string,
  runtimeState: AnnotationTriggerRuntimeState,
): void => {
  runtimeStateById[annotationId] = {
    ...(runtimeStateById[annotationId] ?? {}),
    [triggerId]: runtimeState,
  };
};

export const effectStatePatch = (
  effect: AnnotationTriggerEffect,
): AnnotationStateRecord | undefined =>
  effect.type === 'state_change' ? cloneStateRecord(effect.payload.state) : undefined;

export const shouldFireTrigger = (params: {
  trigger: AnnotationTrigger;
  currentState: AnnotationStateRecord | undefined;
  eventDetail: AnnotationStateRecord | undefined;
  occupancy: AnnotationConditionOccupancySnapshot | undefined;
  action?: string;
  runtimeState: AnnotationTriggerRuntimeState | undefined;
  nowMs: number;
}): boolean => {
  if (
    params.trigger.event === 'interact' &&
    params.trigger.action &&
    normalizeActionToken(params.action ?? '') !== normalizeActionToken(params.trigger.action)
  ) {
    return false;
  }

  if (
    !annotationConditionGroupMatches({
      conditions: params.trigger.conditions,
      currentState: params.currentState,
      eventDetail: params.eventDetail,
      occupancy: params.occupancy,
      nowMs: params.nowMs,
      triggerEvent: params.trigger.event,
    })
  ) {
    return false;
  }

  const dispatchAction = resolveEffectAction(params.trigger);
  if (!shouldRespectCooldown(dispatchAction)) {
    return true;
  }

  const cooldownMs = Math.max(0, Math.floor(params.trigger.cooldownMs ?? 0));
  if (cooldownMs <= 0) {
    return true;
  }

  if (typeof params.runtimeState?.lastFiredAtMs !== 'number') {
    return true;
  }

  return params.nowMs - params.runtimeState.lastFiredAtMs >= cooldownMs;
};

export const applyAnnotationStatePatch = (params: {
  annotationsById: Map<string, WorldAnnotation>;
  runtimeStateById: AnnotationRuntimeStateById;
  annotationId: string;
  patch: AnnotationStateRecord | undefined;
}): void => {
  if (!params.patch || Object.keys(params.patch).length === 0) {
    return;
  }

  const annotation = params.annotationsById.get(params.annotationId);
  if (!annotation) {
    return;
  }

  const nextState = mergeStateRecord(annotation.state, params.patch);
  params.annotationsById.set(params.annotationId, {
    ...annotation,
    ...(nextState ? { state: nextState } : { state: undefined }),
  });

  const nextRuntimeState = mergeStateRecord(
    params.runtimeStateById[params.annotationId],
    params.patch,
  );
  if (nextRuntimeState && Object.keys(nextRuntimeState).length > 0) {
    params.runtimeStateById[params.annotationId] = nextRuntimeState;
  } else {
    params.runtimeStateById[params.annotationId] = {};
  }
};

export const compactAnnotationRuntimeStateById = (
  value: AnnotationRuntimeStateById,
): AnnotationRuntimeStateById | undefined => {
  const entries = Object.entries(value).filter(([, record]) => Object.keys(record).length > 0);
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
};

export const collectUpdatedAnnotations = (params: {
  sourceAnnotations: readonly WorldAnnotation[];
  annotationsById: Map<string, WorldAnnotation>;
  changedAnnotationIds: Set<string>;
}): WorldAnnotation[] =>
  params.sourceAnnotations
    .filter((annotation) => params.changedAnnotationIds.has(annotation.id))
    .map((annotation) => params.annotationsById.get(annotation.id) ?? annotation);

export interface AnnotationTriggerChainResult {
  annotations: WorldAnnotation[];
  updatedAnnotations: WorldAnnotation[];
  runtimeStateById?: AnnotationRuntimeStateById;
  triggerRuntimeStateById?: AnnotationTriggerRuntimeStateByAnnotationId;
  stateChangedByAnnotationId?: Record<string, AnnotationStateRecord>;
  triggerEffects: AppliedAnnotationTriggerEffect[];
}
